import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { TeamMemberWithUser, AgentStatus } from '@/types/chat.types'

interface AgentAvatarProps {
	agent: TeamMemberWithUser | null | undefined
	agentStatus?: AgentStatus | null
	size?: 'xs' | 'sm' | 'md' | 'lg'
	showStatus?: boolean
	className?: string
}

const sizeClasses = {
	xs: 'h-6 w-6 text-[10px]',
	sm: 'h-8 w-8 text-xs',
	md: 'h-10 w-10 text-sm',
	lg: 'h-12 w-12 text-base',
}

const statusSizeClasses = {
	xs: 'h-1.5 w-1.5 border',
	sm: 'h-2 w-2 border-2',
	md: 'h-2.5 w-2.5 border-2',
	lg: 'h-3 w-3 border-2',
}

export function AgentAvatar({
	agent,
	agentStatus,
	size = 'sm',
	showStatus = true,
	className,
}: AgentAvatarProps) {
	if (!agent) {
		return (
			<Avatar className={cn(sizeClasses[size], className)}>
				<AvatarFallback className="bg-muted text-muted-foreground">
					?
				</AvatarFallback>
			</Avatar>
		)
	}

	// Handle both flat structure (assigned_agent from backend) and nested structure (TeamMemberWithUser)
	const agentData = 'user' in agent ? agent.user : agent
	const name = agentData.name
	const email = agentData.email
	const avatarUrl = agentData.avatar_url || (agentData as any).avatar

	const initials = name
		? name
				.split(' ')
				.map((n) => n[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: email[0].toUpperCase()

	const statusColor =
		agentStatus?.status === 'online'
			? 'bg-green-500'
			: agentStatus?.status === 'away'
			? 'bg-yellow-500'
			: 'bg-gray-400'

	return (
		<div className="relative inline-block">
			<Avatar className={cn(sizeClasses[size], className)}>
				{avatarUrl && (
					<AvatarImage src={avatarUrl} alt={name || email} />
				)}
				<AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold">
					{initials}
				</AvatarFallback>
			</Avatar>
			{showStatus && agentStatus && (
				<div
					className={cn(
						'absolute -bottom-0.5 -right-0.5 rounded-full border-background',
						statusSizeClasses[size],
						statusColor
					)}
				/>
			)}
		</div>
	)
}
